import RequirePermissions from '@/components/auth/requirePermissions';
import usePermissions from '@/hooks/usePermissions';
import { useGetOrganizationEmailSettingsQuery } from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Alert, Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { OrganizationEmailSettingCategories, OrganizationType, ProgramType, UserPermissions } from 'types';

import { InvoiceSetting } from '../Invoice/InvoiceSetting';

import BankAccount from './BankAccount';
import { PaymentPlan } from './PaymentPlan';
import { PaymentPlanHistory } from './PaymentPlanHistory';
import { PendingPlanChangeToAccept } from './PendingPlanChangeToAccept';

interface PaymentSettingsProps {
  organization: OrganizationNode;
  isLoading?: boolean;
  programType: ProgramType;
}

export const PaymentSettings = ({ organization, programType }: PaymentSettingsProps) => {
  const { showBankAccountSettingsSection = false } = useFlags(); //Following FIN-2498 requirement we hide the account tab for the time being.
  const permissions = usePermissions();
  const { data: organizationEmails, isLoading: isLoadingEmailSettings } = useGetOrganizationEmailSettingsQuery({
    alias: organization.alias,
  });

  const signatorySetting = organizationEmails?.emailSettings?.find(
    (item) => item.category === OrganizationEmailSettingCategories.signatory,
  );
  const orgHasSignatoryEmails = (signatorySetting?.emails?.length ?? 0) > 0;
  const showBankAccountAndPaymentOrgSettingsSection = permissions.some((permission) =>
    [UserPermissions.admin, UserPermissions.orgAccountingAdmin].includes(permission),
  );
  const isMaterialsVendor = organization.organizationTypes?.includes(OrganizationType.materials);

  return (
    <Box
      childGap={{
        base: 'sm',
        tablet: 'md',
        desktop: 'lg',
      }}
      padding="lg 0 0 0"
    >
      {!orgHasSignatoryEmails && !isLoadingEmailSettings && !isMaterialsVendor ? (
        <Alert variant="warning" message="This Payment Plan cannot be edited without any signatory emails." />
      ) : null}
      <Box flex="auto" margin={{ base: 'md 0 0 0', desktop: 'md 0 0 0', tablet: 'md 0 0 0' }}>
        <PendingPlanChangeToAccept organization={organization} programType={programType} />
      </Box>

      {isMaterialsVendor && (
        <Box>
          <RequirePermissions
            permissions={[UserPermissions.lightReachAccountingAdmin, UserPermissions.lightReachAccountingSettings]}
          >
            <InvoiceSetting organization={organization} programType={programType} />
          </RequirePermissions>
        </Box>
      )}
      {!isMaterialsVendor && (
        <>
          <Box flex="auto" margin={{ base: '0 0 sm 0', desktop: '0 0 lg 0', tablet: '0 0 md 0' }}>
            <PaymentPlan
              organization={organization}
              programType={programType}
              orgHasSignatoryEmails={orgHasSignatoryEmails}
            />
          </Box>
          <Box flex="auto">
            <PaymentPlanHistory organization={organization} programType={programType} />
          </Box>
        </>
      )}
      {showBankAccountSettingsSection && showBankAccountAndPaymentOrgSettingsSection && (
        <Box flex="auto" margin={{ base: 'md 0 0', desktop: 'lg 0 0', tablet: 'md 0 0' }}>
          <BankAccount organization={organization} />
        </Box>
      )}
    </Box>
  );
};
